/**
 * Sending the customer back to the traveller field that failed validation.
 *
 * `validateCheckoutStep` runs against the cart data, far from the DOM, so it
 * reports *which* booking is incomplete rather than trying to hold a reference
 * to an element. A booking is identified by its cart item id plus its
 * `quantityIndex` (traveller 1, 2, … within that item).
 *
 * One row can cover several cart items — a product booked across multiple dates
 * shares one traveller block — so each rendered block advertises every item id it
 * represents in `data-traveller-items`, and the lookup matches on membership.
 */

/** Marker attributes carried by each rendered traveller block. */
export const TRAVELLER_ITEMS_ATTR = "data-traveller-items";
export const TRAVELLER_INDEX_ATTR = "data-quantity-index";

/** Class applied briefly to the offending block; styled in CheckoutStep2. */
export const TRAVELLER_INVALID_CLASS = "traveller-invalid";

/** How long the red border stays up before fading out, in milliseconds. */
export const TRAVELLER_INVALID_DURATION = 6000;

/**
 * Delay before scrolling. The failed click re-renders the step and mounts the
 * error toast; a smooth scroll started in the same tick gets cancelled by that
 * work, which is why the highlight appeared but the page stayed put.
 */
const SCROLL_DELAY = 50;

/** The attribute value a block carries for the item ids it represents. */
export const travellerItemsAttrValue = (itemIds: Array<string | number>): string =>
    itemIds.map((id) => String(id)).join(",");

/** Whether a block advertising `attrValue` represents `itemId`. */
export const travellerBlockMatches = (
    attrValue: string | null | undefined,
    itemId: string | number,
): boolean => {
    if (!attrValue) return false;
    const wanted = String(itemId).trim();
    if (wanted === "") return false;
    return attrValue
        .split(",")
        .map((id) => id.trim())
        .includes(wanted);
};

/**
 * Scrolls the offending traveller block into view and flags it red for a few
 * seconds. No-op when the block can't be found — the toast still carries the
 * message, so a failed lookup degrades to today's behaviour rather than
 * swallowing the error.
 */
export const focusInvalidTravellerField = (
    itemId: string | number | undefined,
    quantityIndex: number | undefined,
): boolean => {
    if (itemId === undefined || quantityIndex === undefined) return false;
    if (typeof document === "undefined") return false;

    const blocks = Array.from(document.querySelectorAll<HTMLElement>(`[${TRAVELLER_ITEMS_ATTR}]`));
    const target = blocks.find(
        (el) =>
            travellerBlockMatches(el.getAttribute(TRAVELLER_ITEMS_ATTR), itemId) &&
            el.getAttribute(TRAVELLER_INDEX_ATTR) === String(quantityIndex),
    );
    if (!target) return false;

    // scrollIntoView rather than window.scrollTo: it walks whichever ancestor
    // actually scrolls, and centring the block sidesteps the fixed header
    // instead of guessing its height.
    window.setTimeout(
        () => target.scrollIntoView({ behavior: "smooth", block: "center" }),
        SCROLL_DELAY,
    );

    target.classList.add(TRAVELLER_INVALID_CLASS);
    window.setTimeout(
        () => target.classList.remove(TRAVELLER_INVALID_CLASS),
        TRAVELLER_INVALID_DURATION,
    );
    return true;
};
